import { Box, alpha, lighten, useTheme } from "@mui/material";

import { CSSProperties, FC, ReactNode, useContext, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";

import { ASSISTANT_WIDGET_Z_INDEX, type chatBoxOption } from "@/components/Chat";
// import FormAccessRequest from "@/components/FormAccessRequest";
import { SplashScreen } from "@/components/Loading/SplashScreen";
import OnboardingOverlay from "@/components/Onboarding/OnboardingOverlay";
// import Popup from "@/components/Popup";
import Scrollbar from "@/components/Scrollbar";
// import { SPJWT } from "@/configs";
import { ChatAIContext } from "@/contexts/ChatAIContext";
import { SidebarContext, widthMenuHide } from "@/contexts/SidebarContext";
// import { togglePopup } from "@/slices/Popup/popupReducer";
import { useSelector } from "@/store";
import { rootReducerName } from "@/store/rootReducer";
// import { getCookie } from "@/utils/session";
import useSize from "@/utils/useSize";
import { useTranslation } from "next-i18next";
import dynamic from "next/dynamic";
import { useRouter } from "next/router";
// import getConfig from "next/config";
import PropTypes from "prop-types";

import Header from "./Header";
import HeaderMobile from "./Header/HeaderMobile";
import Sidebar from "./Sidebar";

const ChatBoxLegacy = dynamic(() => import("@/components/Chat"), { ssr: false });

interface SidebarLayoutProps {
  children?: ReactNode;
  chatBoxOption?: chatBoxOption;
  setScrollbarsRef?: (ref: Scrollbars) => void;
  setupLocked?: boolean;
}

const SidebarLayout: FC<SidebarLayoutProps> = ({
  children,
  chatBoxOption = "hidden",
  setScrollbarsRef,
  setupLocked = false
}) => {
  const { menuState } = useContext(SidebarContext);
  const { isShowChatBoxTask, showChatbox } = useContext(ChatAIContext);
  const theme = useTheme();
  const size = useSize();
  const router = useRouter();
  const { t } = useTranslation("common");
  const [chatboxHeight, setChatboxHeight] = useState<number>(350);
  // const isOpen = useSelector((state) => state[rootReducerName.popup].isOpen);
  const user = useSelector((state) => state[rootReducerName.auth].user);
  const loading = useSelector((state) => state[rootReducerName.auth].loading);
  // const dispatch = useDispatch();
  const shouldRenderChatbox = chatBoxOption !== "hidden" || showChatbox;

  const styleChatbox: CSSProperties = {
    zIndex: ASSISTANT_WIDGET_Z_INDEX,
    position: "fixed",
    bottom: 0,
    // width: size.width > 1280 ? `calc(100% - ${theme.sidebar.width})` : "100%"
    width: size.width > 1280 ? `calc(100% - ${menuState ? theme.sidebar.width : widthMenuHide})` : "100%"
  };

  // On /auth/* routes, render only children (no layout, no popups, no skeletons)
  const isAuthRoute =
    (router.pathname && router.pathname.startsWith("/auth")) || (router.asPath && router.asPath.startsWith("/auth"));
  if (isAuthRoute) {
    return <>{children}</>;
  }
  if (loading && !user) {
    return <SplashScreen message={t("Signing you in...")} />;
  }

  return (
    <Box
      sx={{
        flex: 1,
        height: "100vh",
        ".MuiPageTitle-wrapper": {
          background: theme.palette.mode === "dark" ? theme.colors.alpha.trueWhite[5] : theme.colors.alpha.white[50],
          marginBottom: `${theme.spacing(4)}`,
          boxShadow:
            theme.palette.mode === "dark"
              ? `0 1px 0 ${alpha(
                  lighten(theme.colors.primary.main, 0.7),
                  0.15
                )}, 0px 2px 4px -3px rgba(0, 0, 0, 0.2), 0px 5px 12px -4px rgba(0, 0, 0, .1)`
              : `0px 2px 4px -3px ${alpha(theme.colors.alpha.black[100], 0.1)}, 0px 5px 12px -4px ${alpha(
                  theme.colors.alpha.black[100],
                  0.05
                )}`
        }
      }}
    >
      <Header />
      <Sidebar setupLocked={setupLocked} />
      <Box
        sx={{
          justifyContent: "space-between",
          display: "flex",
          flexWrap: "wrap",
          zIndex: 5,
          flex: 1,
          pt: `${theme.header.height}`,
          [theme.breakpoints.up("lg")]: {
            ml: !menuState ? widthMenuHide : `${theme.sidebar.width}`
          }
        }}
      >
        <div
          style={{
            height: `calc(${size.height - (chatBoxOption === "large" && isShowChatBoxTask ? chatboxHeight : 0)}px - ${theme.header.height})`,
            width: "100%"
            // paddingBottom: "20px"
          }}
        >
          <Scrollbar setRef={setScrollbarsRef}>
            <Box component="main" role="main" id="main-content" tabIndex={-1}>
              <Box
                sx={{
                  display: { sm: "none", xs: "none" },
                  mb: { sm: 0, xs: 2 },
                  pl: { sm: 0, xs: 2 }
                }}
              >
                <HeaderMobile />
              </Box>
              {setupLocked ? null : <OnboardingOverlay />}
              {children}
            </Box>
          </Scrollbar>
        </div>

        {shouldRenderChatbox && (
          <div style={styleChatbox}>
            <ChatBoxLegacy
              onChangeHeight={(h) => {
                chatBoxOption === "large" && setChatboxHeight(h);
              }}
              chatBoxOption={chatBoxOption}
            />
          </div>
        )}
      </Box>
    </Box>
  );
};

SidebarLayout.propTypes = {
  children: PropTypes.node
};

export default SidebarLayout;
